import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../hooks/useAuth';
import { http } from '../lib/http';
import { useSocket } from '../contexts/SocketContext';
import {
  BigBoardData,
  UnitAssignment,
  Unit,
  LoggedInUser,
} from '../types';
import LoginScreen from './LoginScreen';

interface BigBoardProps {
  showHeader?: boolean;
}

const BigBoard: React.FC<BigBoardProps> = ({ showHeader = true }) => {
  const [bigBoardData, setBigBoardData] = useState<BigBoardData | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [error, setError] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());


  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const socket = useSocket();

  const loadData = useCallback(async () => {
    try {
      await Promise.all([
        loadUnits(),
        loadBigBoardData(),
      ]);
      setError(''); // Clear any previous errors
    } catch (error) {
      console.error('Error loading big board data:', error);
      setError('Failed to load big board data');
    }
  }, []);

  // Debug socket availability
  useEffect(() => {
    console.log('[BigBoard] Socket availability changed:', {
      socket: !!socket,
      connected: socket?.connected,
      id: socket?.id,
      isAuthenticated
    });
  }, [socket, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user) {
      loadData(); // Load initial data only once
      
      // No polling - rely on Socket.IO events for real-time updates
      // Socket events will handle all updates instantly
    }
  }, [isAuthenticated, user, loadData]);

  // Update time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Listen for socket events for live updates
  useEffect(() => {
    if (!socket) {
      console.log('[BigBoard] No socket available, waiting...');
      return;
    }

    console.log('[BigBoard] Setting up socket listeners, socket connected:', socket.connected);

    const handleStateUpdate = (newState: any) => {
      console.log('[BigBoard] INSTANT state update received:', newState);
      // Update state immediately without API call for maximum speed
      if (newState && newState.units) {
        setUnits(newState.units);
      }
      if (newState && newState.bigBoardData) {
        setBigBoardData(newState.bigBoardData);
      }
      // Only reload if we don't have complete state data
      if (!newState || !newState.units || !newState.bigBoardData) {
        loadData();
      }
    };

    const handleAssignmentUpdate = () => {
      console.log('[BigBoard] Assignment updated, reloading data');
      loadData();
    };

    const handleShiftUpdate = () => {
      console.log('[BigBoard] Shift updated, reloading data');
      loadData();
    };

    const handleConnect = () => {
      console.log('[BigBoard] Socket connected, joining dispatch room...');
      if (isAuthenticated) {
        socket.emit('join-dispatch');
      }
    };

    // Set up event listeners for MAXIMUM real-time experience
    socket.on('connect', handleConnect);
    socket.on('state-update', handleStateUpdate);
    socket.on('unit-assignment-updated', handleAssignmentUpdate);
    socket.on('unit-assignment-removed', handleAssignmentUpdate);
    socket.on('shift-updated', handleShiftUpdate);
    socket.on('shift-activated', handleShiftUpdate);
    socket.on('shift-deleted', handleShiftUpdate);
    socket.on('shift-created', handleShiftUpdate);
    
    // Additional real-time events for instant updates
    socket.on('call-created', () => {
      console.log('[BigBoard] INSTANT call created - updating immediately');
      loadData();
    });
    socket.on('call-updated', () => {
      console.log('[BigBoard] INSTANT call updated - updating immediately');
      loadData();
    });
    socket.on('unit-updated', () => {
      console.log('[BigBoard] INSTANT unit updated - updating immediately');
      loadData();
    });
    socket.on('unit-claim-updated', () => {
      console.log('[BigBoard] INSTANT unit claim updated - updating immediately');
      loadData();
    });
    socket.on('unit-status-changed', () => {
      console.log('[BigBoard] INSTANT unit status changed - updating immediately');
      loadData();
    });

    // Join dispatch room if already connected
    if (socket.connected && isAuthenticated) {
      console.log('[BigBoard] Socket already connected, joining dispatch room...');
      socket.emit('join-dispatch');
    }

    return () => {
      socket.off('connect', handleConnect);
      socket.off('state-update', handleStateUpdate);
      socket.off('unit-assignment-updated', handleAssignmentUpdate);
      socket.off('unit-assignment-removed', handleAssignmentUpdate);
      socket.off('shift-updated', handleShiftUpdate);
      socket.off('shift-activated', handleShiftUpdate);
      socket.off('shift-deleted', handleShiftUpdate);
      socket.off('shift-created', handleShiftUpdate);
      
      // Clean up additional real-time event listeners
      socket.off('call-created');
      socket.off('call-updated');
      socket.off('unit-updated');
      socket.off('unit-claim-updated');
      socket.off('unit-status-changed');
    };
  }, [socket, isAuthenticated, loadData]);

  const loadBigBoardData = async () => {
    try {
      const response = await http.get('/unit-assignments/big-board');
      if (response.data.success) {
        console.log('[BigBoard] Big board data structure:', {
          assignments: response.data.data.assignments,
          crossStaffing: response.data.data.crossStaffing,
          assignmentsType: typeof response.data.data.assignments,
          crossStaffingType: typeof response.data.data.crossStaffing
        });
        setBigBoardData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading big board data:', error);
    }
  };

  const loadUnits = async () => {
    try {
      const response = await http.get('/units');
      if (response.data.success) {
        setUnits(response.data.data);
      }
    } catch (error) {
      console.error('Error loading units:', error);
    }
  };


  const getUnitTypeColor = (unitType: string) => {
    switch (unitType) {
      case 'ENG':
        return '#d32f2f'; // Red
      case 'TRK':
        return '#f57c00'; // Orange
      case 'TNKR':
        return '#f57c00'; // Orange
      case 'BRSH':
        return '#f57c00'; // Orange
      case 'ALS':
      case 'BLS':
      case 'MED':
        return '#1976d2'; // Blue
      case 'RESQ':
      case 'DIV':
        return '#388e3c'; // Green
      case 'UTIL':
        return '#388e3c'; // Green
      case 'CMD':
        return '#424242'; // Black
      case 'SUPV':
        return '#424242'; // Black
      default:
        return '#9c27b0'; // Purple
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'I/S':
        return { bg: '#4caf50', text: '#ffffff' };
      case 'OOS':
        return { bg: '#d32f2f', text: '#ffffff' };
      default:
        return { bg: '#424242', text: '#ffffff' };
    }
  };

  const getAvailableRolesForUnit = (unitId: string) => {
    const unit = units.find(u => u.id === unitId);
    if (!unit) return [];

    // Define roles based on unit type
    switch (unit.type) {
      case 'ENG':
      case 'TRK':
      case 'TNKR':
      case 'BRSH':
        return ['Engineer', 'Officer', 'Firefighter 1', 'Firefighter 2', 'Firefighter 3'];
      case 'ALS':
        return ['Driver', 'Paramedic', 'EMT 1'];
      case 'BLS':
      case 'MED':
        return ['Driver', 'EMT 1', 'EMT 2'];
      case 'RESQ':
        return ['Engineer', 'Officer', 'Firefighter 1', 'Firefighter 2', 'Firefighter 3'];
      case 'DIV':
        return ['Engineer', 'Officer', 'Technician 1', 'Technician 2'];
      case 'UTIL':
        return ['Engineer', 'Officer', 'Firefighter 1'];
      case 'CMD':
        return ['Commander', 'Officer'];
      case 'SUPV':
        return ['Officer'];
      case 'DISP':
        return ['Dispatcher 1', 'Dispatcher 2'];
      default:
        return ['Driver', 'Officer'];
    }
  };

  const getAssignedPersonnelForRole = (unitId: string, role: string) => {
    if (!bigBoardData?.assignments) return null;
    
    // Handle both map and array formats for assignments
    const assignments = (bigBoardData as any).assignments;
    
    // If assignments is a map (object with unitId keys)
    if (assignments[unitId] && Array.isArray(assignments[unitId])) {
      const assignment = assignments[unitId].find((a: any) => a.role === role);
      if (assignment) {
        // For freeform assignments, show only the personnel name
        return assignment.callsign.startsWith('FREE_') ? assignment.personnelName : assignment.callsign;
      }
      return null;
    }
    
    // If assignments is a flat array
    if (Array.isArray(assignments)) {
      const assignment = assignments
        .filter((a: any) => a.unitId === unitId)
        .find((a: any) => a.role === role);
      if (assignment) {
        // For freeform assignments, show only the personnel name
        return assignment.callsign.startsWith('FREE_') ? assignment.personnelName : assignment.callsign;
      }
      return null;
    }
    
    return null;
  };

  const getCrossStaffingForUnit = (unitId: string) => {
    if (!bigBoardData?.crossStaffing) return [];
    
    const crossStaffing = (bigBoardData as any).crossStaffing;
    
    // If crossStaffing is a map (object with unitId keys)
    if (crossStaffing[unitId] && Array.isArray(crossStaffing[unitId])) {
      return crossStaffing[unitId];
    }
    
    // If crossStaffing is a flat array
    if (Array.isArray(crossStaffing)) {
      return crossStaffing.filter((cs: any) => cs.unitId === unitId);
    }
    
    return [];
  };

  const getAssignmentsForUnit = (unitId: string) => {
    if (!bigBoardData?.assignments) return [];
    
    const assignments = (bigBoardData as any).assignments;
    
    // If assignments is a map (object with unitId keys)
    if (assignments[unitId] && Array.isArray(assignments[unitId])) {
      return assignments[unitId];
    }
    
    // If assignments is a flat array
    if (Array.isArray(assignments)) {
      return assignments.filter((a: any) => a.unitId === unitId);
    }
    
    return [];
  };

  type DisplayUnit = Unit & {
    displayName?: string;
    assignments?: UnitAssignment[];
    crossStaffing?: { id?: string; crossUnitId: string }[];
    isUserCard?: boolean;
    isReadOnly?: boolean;
    user?: LoggedInUser;
    claimedBy?: LoggedInUser[];
    claims?: LoggedInUser[];
  };

  const renderUnitCard = (unit: DisplayUnit) => {
    // Handle user cards (available personnel)
    if (unit.isUserCard && unit.user) {
      return (
        <Card
          key={unit.id}
          sx={{
            height: '100%',
            border: '2px solid #4caf50',
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <CardContent sx={{ p: 1.5 }}>
            {/* User Header */}
            <Box
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                p: 1,
                mb: 1,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {unit.displayName}
              </Typography>
            </Box>

            {/* User Info */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                <strong>Username:</strong> {unit.user.username}
              </Typography>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                <strong>Role:</strong> {unit.user.role}
              </Typography>
              {unit.user.claimedUnit && (
                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#ff9800' }}>
                  <strong>Claimed Unit:</strong> {unit.user.claimedUnit}
                </Typography>
              )}
            </Box>

            {/* Available Status */}
            <Box
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                p: 0.5,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                AVAILABLE FOR ASSIGNMENT
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Handle claimed units
    if (unit.claimedBy && unit.claimedBy.length > 0) {
      const statusColor = getStatusColor(unit.status);
      const unitTypeColor = getUnitTypeColor(unit.type);

      return (
        <Card
          key={unit.id}
          sx={{
            height: '100%',
            border: '2px solid #ff9800',
            '&:hover': {
              boxShadow: 4,
            },
          }}
        >
          <CardContent sx={{ p: 1.5 }}>
            {/* Unit Header */}
            <Box
              sx={{
                backgroundColor: '#ff9800',
                color: 'white',
                p: 1,
                mb: 1,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {unit.displayName || unit.id}
              </Typography>
            </Box>

            {/* Status */}
            <Box
              sx={{
                backgroundColor: statusColor.bg,
                color: statusColor.text,
                p: 0.5,
                mb: 1,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                STATUS: {unit.status}
              </Typography>
            </Box>

            {/* Claimed By */}
            <Box sx={{ mb: 1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', mb: 0.5 }}>
                CLAIMED BY:
              </Typography>
              {unit.claimedBy.map((user, idx) => (
                <Typography
                  key={idx}
                  variant="body2"
                  sx={{
                    fontSize: '0.75rem',
                    color: '#4caf50',
                    fontWeight: 'bold',
                    mb: 0.5
                  }}
                >
                  {user.robloxName || user.username} ({user.role})
                </Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      );
    }

    // Handle regular unit cards (with or without assignments)
    const statusColor = getStatusColor(unit.status);
    const unitTypeColor = getUnitTypeColor(unit.type);
    const availableRoles = getAvailableRolesForUnit(unit.id);
    const crossStaffing = getCrossStaffingForUnit(unit.id);

    return (
      <Card
        key={unit.id}
        sx={{
          height: '100%',
          border: `2px solid ${unitTypeColor}`,
          opacity: unit.isReadOnly ? 0.7 : 1,
          '&:hover': {
            boxShadow: 4,
          },
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          {/* Unit Header */}
          <Box
            sx={{
              backgroundColor: unitTypeColor,
              color: 'white',
              p: 1,
              mb: 1,
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {unit.displayName || unit.id}
            </Typography>
            {unit.isReadOnly && (
              <Typography variant="body2" sx={{ fontSize: '0.7rem', opacity: 0.8 }}>
                READ ONLY
              </Typography>
            )}
          </Box>

          {/* Status */}
          <Box
            sx={{
              backgroundColor: statusColor.bg,
              color: statusColor.text,
              p: 0.5,
              mb: 1,
              borderRadius: 1,
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              STATUS: {unit.status}
            </Typography>
          </Box>

          {/* Personnel Assignments */}
          <Box sx={{ mb: 1 }}>
            {availableRoles.map((role) => {
              const assignedPersonnel = getAssignedPersonnelForRole(unit.id, role);
              return (
                <Box key={role} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="body2" sx={{ minWidth: 80, fontSize: '0.75rem' }}>
                    {role}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      color: assignedPersonnel ? '#4caf50' : '#f44336',
                      fontWeight: 'bold',
                    }}
                  >
                    {assignedPersonnel || 'Unassigned'}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Cross Staffing */}
          {crossStaffing.length > 0 && (
            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
              <Typography variant="body2" sx={{ fontSize: '0.75rem', fontWeight: 'bold', mb: 0.5 }}>
                X STAFFING:
              </Typography>
              {crossStaffing.map((cs, idx) => (
                <Box
                  key={cs.id ?? `${cs.crossUnitId}-${idx}`}
                  sx={{
                    backgroundColor: '#4caf50',
                    color: 'white',
                    p: 0.5,
                    borderRadius: 1,
                    mb: 0.5,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                    {cs.crossUnitId}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };

  const getDisplayName = (unitId: string) => {
    // Map unit IDs to display names for the Big Board
    const displayNames: { [key: string]: string } = {
      'E11': 'ENGINE 11',
      'T23': 'TOWER 23',
      'TN32': 'TANKER 32',
      'A73': 'MEDIC 73',
      'A78': 'MEDIC 78',
      'SAF55': 'SAFETY 55',
      'CMD90': 'COMMAND 90',
      'EMS87': 'EMS 87',
      'BR28': 'BRUSH 28',
      'E35': 'ENGINE 35',
      'RA4': 'RESERVE AMBULANCE 4',
      'R1': 'RESCUE 1',
      'H38': 'HAZMAT 38',
      'SAR27': 'SAR27',
      'RED1': 'RED (ENGINE) 1',
      'RED2': 'RED (TRUCK) 2',
      'RED3': 'RED (UTILITY) 3',
      'RED4': 'RED (COMMAND) 4',
      'BLS1': 'BLS 1',
      'ALS1': 'ALS 1',
      'MED1': 'MED 1',
      'BOAT1': 'BOAT 1'
    };
    return displayNames[unitId] || unitId;
  };

  // Helper function to get users without assignments
  const getUsersWithoutAssignments = () => {
    if (!bigBoardData?.loggedInUsers) return [];
    
    return bigBoardData.loggedInUsers.filter(user => {
      // Check if user has any assignments for current shift
      if (bigBoardData.shift) {
        const hasAssignment = Object.values(bigBoardData.assignments || {}).some(assignments => 
          assignments.some(assignment => assignment.userId === user.id)
        );
        return !hasAssignment;
      }
      // If no active shift, show all logged in users without claimed units
      return !user.claimedUnit;
    });
  };

  // Helper function to get claimed units with their users
  const getClaimedUnitsData = () => {
    if (!bigBoardData?.claimedUnits) return [];
    
    const claimedUnitsList: any[] = [];
    Object.entries(bigBoardData.claimedUnits).forEach(([unitId, users]) => {
      if (users && users.length > 0) {
        const unit = units.find(u => u.id === unitId);
        if (unit) {
          claimedUnitsList.push({
            ...unit,
            displayName: getDisplayName(unit.id),
            claimedBy: users,
            claims: users
          });
        }
      }
    });
    
    return claimedUnitsList;
  };

  const groupUnitsByStation = () => {
    const stations: { [key: string]: any[] } = {};
    
    // Define unit order for each station
    const stationOneOrder = ['E11', 'T23', 'R1', 'H38', 'TN32', 'A73', 'A78', 'SAF55', 'CMD90', 'EMS87'];
    const stationTwoOrder = ['E35', 'BR28', 'SAR27', 'RA4'];
    const beaumontOrder = ['RED1', 'RED2', 'RED3', 'RED4'];
    const medicalOrder = ['BLS1', 'ALS1', 'MED1'];
    const specialOrder = ['FNG', 'FNG-AIR', 'FNG-CG', 'FSP-MR', 'DISPATCH', 'BOAT1'];
    
    const isShiftActive = bigBoardData?.shift?.isActive;
    
    if (isShiftActive) {
      // When shift is active, show units with assignments only + unassigned logged-in users
      
      // Add units with assignments OR cross-staffing
      if (units && units.length > 0) {
        units
          .filter((unit) => {
            const unitAssignments = getAssignmentsForUnit(unit.id);
            const unitCrossStaffing = getCrossStaffingForUnit(unit.id);
            return unitAssignments.length > 0 || unitCrossStaffing.length > 0;
          })
          .forEach((unit) => {
            let station = 'STATION ONE';
            
            if (medicalOrder.includes(unit.id)) {
              station = 'STAPLETON COUNTY MEDICAL SERVICE';
            } else if (beaumontOrder.includes(unit.id)) {
              station = 'BEAUMONT FIRE';
            } else if (stationTwoOrder.includes(unit.id)) {
              station = 'STATION TWO';
            } else if (specialOrder.includes(unit.id)) {
              station = 'SPECIAL UNITS';
            }

            if (!stations[station]) {
              stations[station] = [];
            }
            
            const unitWithAssignments = {
              ...unit,
              displayName: getDisplayName(unit.id),
              assignments: bigBoardData?.assignments?.[unit.id] || [],
              crossStaffing: bigBoardData?.crossStaffing?.[unit.id] || []
            };
            
            stations[station].push(unitWithAssignments);
          });
      }
      
      // Add section for unassigned logged-in users
      const unassignedUsers = getUsersWithoutAssignments();
      if (unassignedUsers.length > 0) {
        stations['AVAILABLE PERSONNEL'] = unassignedUsers.map(user => ({
          id: `user-${user.id}`,
          type: 'USER',
          status: 'AVAILABLE',
          displayName: user.robloxName || user.username,
          user: user,
          isUserCard: true
        }));
      }
      
    } else {
      // When shift is not active, show read-only view: claimed units + units with assignments
      
      // Add units with assignments/cross-staffing (read-only)
      if (units && units.length > 0) {
        units
          .filter((unit) => {
            const unitAssignments = getAssignmentsForUnit(unit.id);
            const unitCrossStaffing = getCrossStaffingForUnit(unit.id);
            return unitAssignments.length > 0 || unitCrossStaffing.length > 0;
          })
          .forEach((unit) => {
            let station = 'STATION ONE';
            
            if (medicalOrder.includes(unit.id)) {
              station = 'STAPLETON COUNTY MEDICAL SERVICE';
            } else if (beaumontOrder.includes(unit.id)) {
              station = 'BEAUMONT FIRE';
            } else if (stationTwoOrder.includes(unit.id)) {
              station = 'STATION TWO';
            } else if (specialOrder.includes(unit.id)) {
              station = 'SPECIAL UNITS';
            }

            if (!stations[station]) {
              stations[station] = [];
            }
            
            const unitWithAssignments = {
              ...unit,
              displayName: getDisplayName(unit.id),
              assignments: bigBoardData?.assignments?.[unit.id] || [],
              crossStaffing: bigBoardData?.crossStaffing?.[unit.id] || [],
              isReadOnly: true
            };
            
            stations[station].push(unitWithAssignments);
          });
      }
      
      // Add claimed units section
      const claimedUnits = getClaimedUnitsData();
      if (claimedUnits.length > 0) {
        stations['CLAIMED UNITS'] = claimedUnits;
      }
      
      // Add unassigned logged-in users
      const unassignedUsers = getUsersWithoutAssignments();
      if (unassignedUsers.length > 0) {
        stations['AVAILABLE PERSONNEL'] = unassignedUsers.map(user => ({
          id: `user-${user.id}`,
          type: 'USER',
          status: 'AVAILABLE',
          displayName: user.robloxName || user.username,
          user: user,
          isUserCard: true
        }));
      }
    }

    // Sort units within each station
    Object.keys(stations).forEach(stationName => {
      let orderArray: string[] = [];
      switch (stationName) {
        case 'STATION ONE':
          orderArray = stationOneOrder;
          break;
        case 'STATION TWO':
          orderArray = stationTwoOrder;
          break;
        case 'BEAUMONT FIRE':
          orderArray = beaumontOrder;
          break;
        case 'STAPLETON COUNTY MEDICAL SERVICE':
          orderArray = medicalOrder;
          break;
        case 'SPECIAL UNITS':
          orderArray = specialOrder;
          break;
        case 'CLAIMED UNITS':
        case 'AVAILABLE PERSONNEL':
          // Don't sort these sections
          return;
      }
      
      stations[stationName].sort((a, b) => {
        // Skip sorting for user cards
        if (a.isUserCard || b.isUserCard) return 0;
        
        const aIndex = orderArray.indexOf(a.id);
        const bIndex = orderArray.indexOf(b.id);
        if (aIndex === -1) return 1;
        if (bIndex === -1) return -1;
        return aIndex - bIndex;
      });
    });

    return stations;
  };

  const getStationColor = (stationName: string) => {
    switch (stationName) {
      case 'STATION ONE':
      case 'STATION TWO':
        return '#1976d2'; // Blue
      case 'BEAUMONT FIRE':
        return '#d32f2f'; // Red
      case 'STAPLETON COUNTY MEDICAL SERVICE':
        return '#ffa000'; // Yellow
      case 'SPECIAL UNITS':
        return '#424242'; // Dark Gray
      case 'AVAILABLE PERSONNEL':
        return '#4caf50'; // Green
      case 'CLAIMED UNITS':
        return '#ff9800'; // Orange
      default:
        return '#9c27b0'; // Purple
    }
  };

  const stations = groupUnitsByStation();

  // Show loading state while verifying authentication
  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', gap: 2 }}>
        <CircularProgress />
        <Typography>Verifying authentication...</Typography>
      </Box>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated || !user) {
    return <LoginScreen />;
  }

  return (
    <Box sx={{ 
      width: '100%',
      height: showHeader ? '100vh' : '100%',
      overflow: 'auto',
      backgroundColor: '#0d1117'
    }}>
      {/* Fixed Header - only show if showHeader is true */}
      {showHeader && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1000,
            backgroundColor: '#1976d2',
            color: 'white',
            p: 2,
            mb: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          }}
        >
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              BIG BOARD | {units.filter(u => u.status === 'I/S').length} UNITS IN SERVICE
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              {currentTime.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                second: '2-digit',
                timeZoneName: 'short'
              })}
            </Typography>
            <IconButton onClick={() => loadData()} sx={{ color: 'white' }}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Box>
      )}

      {/* Scrollable Content */}
      <Box sx={{ p: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {bigBoardData?.shift && showHeader && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Current Shift: {bigBoardData.shift.name} ({new Date(bigBoardData.shift.startTime).toLocaleDateString()} - {new Date(bigBoardData.shift.endTime).toLocaleDateString()})
          </Alert>
        )}

        {/* Stations */}
        {Object.entries(stations).map(([stationName, stationUnits]) => (
          <Box key={stationName} sx={{ mb: 3 }}>
            {/* Station Header */}
            <Box
              sx={{
                backgroundColor: getStationColor(stationName),
                color: 'white',
                p: 1.5,
                mb: 2,
                borderRadius: 1,
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {stationName}
              </Typography>
            </Box>

            {/* Units Grid */}
            <Grid container spacing={2}>
              {stationUnits.map((unit) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={unit.id}>
                  {renderUnitCard(unit)}
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BigBoard;
